(ns frontend.ux-overhaul.listing.generic.view
  (:require
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.opensearch.days-since :as days-since]
   [common.opensearch.kanban-sort :as kanban-sort]
   [common.opensearch.totals :as totals]
   [common.opensearch.utils :as os-utils]
   [common.schema.listing-definition :as listing-definition]
   [common.schema.registry :as registry]
   [frontend.ux-overhaul.listing.generic.event :as event]
   [frontend.ux-overhaul.listing.generic.group-by :as group-by]
   [frontend.ux-overhaul.listing.generic.header :as header]
   [frontend.ux-overhaul.listing.generic.model :as model]
   [frontend.ux-overhaul.listing.generic.selection :as selection]
   [frontend.ux-overhaul.listing.generic.sub :as sub]
   [frontend.ux-overhaul.listing.generic.toolbar :refer [close-on-escape-handler field-label field-path-key
                                                         field-path-normalized]]
   [frontend.ux-overhaul.listing.preview.sub :as preview-sub]
   [frontend.ux-overhaul.listing.preview.view :as preview]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.avatar.view :as avatar]
   [ui.components.button.view :as button]
   [ui.components.icon-choice.view :as icon-choice]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.input.search :as search-input]
   [ui.components.radio-group.view :as radio-group]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.selection-pill.view :as selection-pill]
   [ui.components.toolbar-button.view :as toolbar-button]
   [ui.dom-globals :as dg]
   [ui.editors.simple-filters.event :as simple-filters-event]
   [ui.editors.simple-filters.sub :as simple-filters-sub]
   [ui.editors.simple-filters.view :as simple-filters]
   [ui.icons :as icons]
   [ui.view.grid-opensearch.columns-modal.event :as columns-modal-event]
   [ui.view.grid-opensearch.density :as grid-density]
   [ui.view.grid-opensearch.event :as grid-opensearch-event]
   [ui.view.grid-opensearch.sub :as grid-opensearch-sub]
   [ui.view.grid-opensearch.view :as grid-opensearch]
   [ui.view.kanban-opensearch.view :as kanban-opensearch]
   [ui.view.list-opensearch.view :as list-opensearch]
   [ui.view.opensearch-value.currency :as currency]))

(def default-view-mode-items
  [{:value :kanban :icon [:i.fal.fa-columns] :title (loc/tr :kanban)}
   {:value :grid :icon [:i.fal.fa-th] :title (loc/tr :grid)}
   {:value :gallery :icon [:i.fal.fa-image] :title (loc/tr :gallery)}
   {:value :list :icon [:i.fal.fa-list] :title (loc/tr :list)}])

(defn- filters-editor-id
  [listing-id filters-config]
  (or (:editor-id filters-config)
      (str listing-id "-filters")))

(defn- mode-view-id
  [listing-id view-mode]
  (str listing-id "-" (name view-mode)))


(defn- grid-status-bar
  "The line under the grid: the number of the rows that the search finds, and the
   number of the selected rows."
  [{:keys [grid-id listing-id]}]
  (let [row-total @(rf/subscribe [::grid-opensearch-sub/row-total grid-id])
        selected-count @(rf/subscribe [::sub/selected-count listing-id])]
    (when (some? row-total)
      [:div {:class "tw2-flex tw2-items-center tw2-gap-2 tw2-pt-2 tw2-text-xs tw2-text-tertiary"}
       [:span (loc/tr-item-count row-total)]
       (when (pos? (or selected-count 0))
         [:<>
          [:span "·"]
          [:span (loc/tr :grid-status/selected [selected-count])]])])))

(defn- default-render-body
  [{:keys [listing-id entity-type pipeline-id view-mode context query-text initialized?
           group-by-field
           row-group-by-field
           row-group-sort-mode
           kanban-column-sort-mode
           kanban-card-sort-mode
           hide-empty-columns?
           collapse-command
           on-collapse-state-changed
           card-show-labels?
           card-body-fields
           toolbar-filters-by-field-path on-row-clicked on-card-clicked
           on-record-preview preview-entity-id
           on-create-first-item
           available-view-modes
           view-overrides]}]
  (if (or (not initialized?)
          (nil? entity-type))
    [:div {:class "p-4 text-sm text-base-light-text-paler"}
     "Loading listing..."]
    (let [effective-view-mode (if (contains? (set available-view-modes) view-mode)
                                view-mode
                                (model/preferred-view-mode available-view-modes))
          mode-overrides (get view-overrides effective-view-mode {})
          normalize-field-path (fn [field-path]
                                 (cond
                                   (vector? field-path) field-path
                                   (keyword? field-path) [field-path]
                                   (sequential? field-path) (vec field-path)
                                   :else nil))
          accessible-field-paths (->> (or (:accessible-fields context) [])
                                      (keep :field-path)
                                      (keep normalize-field-path)
                                      set)
          apply-card-overrides (fn [card-config]
                                 (let [base-body-fields (->> (or (:body-fields card-config) [])
                                                             (keep normalize-field-path)
                                                             vec)
                                       sanitized-body-fields (->> (or card-body-fields [])
                                                                  (keep normalize-field-path)
                                                                  (filter accessible-field-paths)
                                                                  distinct
                                                                  vec)
                                       body-fields (model/card-body-fields
                                                    {:chosen-fields     card-body-fields
                                                     :accessible-fields sanitized-body-fields
                                                     :default-fields    base-body-fields})]
                                   (cond-> card-config
                                     true (assoc :body-fields body-fields)
                                     true (assoc :show-labels? (boolean card-show-labels?))
                                     ;; A card shows a currency value with the currency of
                                     ;; its row, so it asks for the field that holds the
                                     ;; currency. This is not in :meta-fields, as the toolbar
                                     ;; search looks in those fields.
                                     true (assoc :currency-fields
                                                 (currency/card-currency-field-paths
                                                  {:card-config            (assoc card-config :body-fields body-fields)
                                                   :tenant-schema          @session/tenant-schema
                                                   :accessible-field-paths accessible-field-paths}))
                                     ;; A field that the tenant disables is not
                                     ;; accessible, and a request for it fails the
                                     ;; whole listing. Ask only for what is accessible.
                                     (seq (:meta-fields card-config))
                                     (assoc :meta-fields (->> (:meta-fields card-config)
                                                              (keep normalize-field-path)
                                                              (filter accessible-field-paths)
                                                              vec)))))
          scoped-query-context (fn [field-paths]
                                 (assoc (or context {})
                                        :entity-type
                                        entity-type
                                        :query-field-paths
                                        (->> field-paths
                                             (keep normalize-field-path)
                                             distinct
                                             vec)))
          search-results-card-config (-> (listing-definition/card-config entity-type effective-view-mode)
                                         (apply-card-overrides))
          search-results-query-context (scoped-query-context
                                        (concat (listing-definition/card-config-field-paths search-results-card-config)
                                                [row-group-by-field]))
          search-results-config (-> (listing-definition/search-results-config
                                     {:entity-type                   entity-type
                                      :pipeline-id                   pipeline-id
                                      :row-group-by-field            nil
                                      :row-group-sort-mode           row-group-sort-mode
                                      :hide-empty-columns?           hide-empty-columns?
                                      :kanban-column-sort-mode       kanban-column-sort-mode
                                      :view-mode                     effective-view-mode
                                      :context                       search-results-query-context
                                      :query-text                    query-text
                                      :toolbar-filters-by-field-path toolbar-filters-by-field-path})
                                    (assoc :card-config search-results-card-config))
          kanban-card-config (-> (listing-definition/card-config entity-type :kanban)
                                 (apply-card-overrides))
          kanban-query-context (scoped-query-context
                                (concat (listing-definition/card-config-field-paths kanban-card-config)
                                        [group-by-field
                                         row-group-by-field]))
          kanban-config (-> (listing-definition/kanban-config
                             {:entity-type                   entity-type
                              :pipeline-id                   pipeline-id
                              :group-by-field                group-by-field
                              :row-group-by-field            row-group-by-field
                              :row-group-sort-mode           row-group-sort-mode
                              :kanban-column-sort-mode       kanban-column-sort-mode
                              :kanban-card-sort-mode         kanban-card-sort-mode
                              :sum-totals                    @(rf/subscribe [::sub/kanban-sum-totals listing-id])
                              :hide-empty-columns?           hide-empty-columns?
                              :context                       kanban-query-context
                              :query-text                    query-text
                              :toolbar-filters-by-field-path toolbar-filters-by-field-path})
                            (assoc :card-config kanban-card-config))]
      (case effective-view-mode
        :grid
        [:div {:class "flex h-full min-h-0 flex-col p-4"}
         ^{:key (mode-view-id listing-id :grid)}
         [grid-opensearch/view
          (merge {:grid-id                        (mode-view-id listing-id :grid)
                  :entity-type                    entity-type
                  :pipeline-id                    pipeline-id
                  :accessible-field-paths         (mapv :field-path (or (:accessible-fields context) []))
                  :external-query-text            query-text
                  :external-filters-by-field-path toolbar-filters-by-field-path
                  :external-filter-context        context
                  :on-row-clicked                 on-row-clicked
                  :on-row-preview                 on-record-preview
                  :active-entity-id               preview-entity-id
                  :on-create-first-item           on-create-first-item
                  :group-by-field                 row-group-by-field
                  :row-group-sort-mode            row-group-sort-mode
                  :hide-empty-columns?            hide-empty-columns?
                  :groupable-field-paths          (or (:groupable-field-paths context) #{})
                  :on-group-by-changed            (fn [field-path]
                                                    (if (some? field-path)
                                                      (rf/dispatch [::event/set-row-group-by-field listing-id field-path])
                                                      (rf/dispatch [::event/clear-row-group-by-field listing-id])))
                  :on-grid-ready                  (fn [api]
                                                    (rf/dispatch [::event/restore-grid-column-filters listing-id api]))
                  :on-filter-model-changed        (fn [filter-model]
                                                    (rf/dispatch [::event/set-grid-column-filters listing-id filter-model]))
                  :on-selection-changed           (fn [selected-ids & [opts]]
                                                    (rf/dispatch [::event/set-selected-ids
                                                                  listing-id
                                                                  selected-ids
                                                                  opts]))
                  :on-select-all-changed          (fn [{:keys [excluded-ids]}]
                                                    (rf/dispatch [::event/set-all-selection
                                                                  listing-id
                                                                  {:grid-id      (mode-view-id listing-id :grid)
                                                                   :excluded-ids excluded-ids}]))}
                 mode-overrides)]
         [grid-status-bar {:grid-id    (mode-view-id listing-id :grid)
                           :listing-id listing-id}]]

        :kanban
        [:div {:class "flex h-full min-h-0 flex-col p-4"}
         ^{:key (mode-view-id listing-id :kanban)}
         [kanban-opensearch/view
          (merge {:kanban-id                 (mode-view-id listing-id :kanban)
                  :config                    kanban-config
                  :collapse-command          collapse-command
                  :on-create-first-item      on-create-first-item
                  :on-collapse-state-changed on-collapse-state-changed
                  :on-selection-changed      (fn [selected-ids & _]
                                               (rf/dispatch [::event/set-selected-ids
                                                             listing-id
                                                             selected-ids]))
                  :on-card-clicked           on-card-clicked
                  :on-card-preview           on-record-preview
                  :active-entity-id          preview-entity-id}
                 mode-overrides)]]

        :list
        [:div {:class "flex h-full min-h-0 flex-col"}
         ^{:key (mode-view-id listing-id :list)}
         [list-opensearch/view
          (merge {:list-id                   (mode-view-id listing-id :list)
                  :config                    search-results-config
                  :layout                    :list
                  :collapse-command          collapse-command
                  :on-collapse-state-changed on-collapse-state-changed
                  :on-selection-changed      (fn [selected-ids & _]
                                               (rf/dispatch [::event/set-selected-ids
                                                             listing-id
                                                             selected-ids]))
                  :on-card-clicked           on-card-clicked}
                 mode-overrides)]]

        :gallery
        [:div {:class "flex h-full min-h-0 flex-col"}
         ^{:key (mode-view-id listing-id :gallery)}
         [list-opensearch/view
          (merge {:list-id                   (mode-view-id listing-id :gallery)
                  :config                    search-results-config
                  :layout                    :gallery
                  :collapse-command          collapse-command
                  :on-collapse-state-changed on-collapse-state-changed
                  :on-selection-changed      (fn [selected-ids & _]
                                               (rf/dispatch [::event/set-selected-ids
                                                             listing-id
                                                             selected-ids]))
                  :on-card-clicked           on-card-clicked}
                 mode-overrides)]]

        [:div {:class "p-4 text-sm text-base-light-text-paler"}
         (str "Listing body for mode: "
              (or (some-> effective-view-mode name) "unknown"))]))))

(defn- query-input
  [{:keys [listing-id query-text disabled? placeholder]}]
  [:div {:class "tw2-w-full"}
   [search-input/view
    {:value     (or query-text "")
     :on-change (fn [value]
                  (when-not disabled?
                    (rf/dispatch [::event/set-query-draft listing-id value])))}]
   (when placeholder
     [:div {:class "hidden"} placeholder])])

(defn- dispatch-action-event
  "Dispatch the event of a toolbar action. An action that works on the selected
   rows first lets the listing resolve a selection of all rows to ids."
  [{:keys [listing-id requires-selection? event]}]
  (rf/dispatch (if requires-selection?
                 [::event/with-resolved-selection
                  {:listing-id listing-id
                   :then       event}]
                 event)))

(defn- toolbar-action-dropdown-item
  [{:keys [listing-id disabled?]} {:keys [id label enabled? on-click event items
                                          custom-item? custom-class value tooltip-text
                                          section-label? requires-selection?]
                                   :as _action}]
  (let [disabled? (or disabled? (false? enabled?))
        item {:label        (or label (some-> id name) "")
              :disabled?    disabled?
              :custom-item? custom-item?
              :item-class   custom-class
              :value        value
              :tooltip      tooltip-text
              ;; A non-actionable group header, rendered by the dropdown as an
              ;; uppercase label rather than a selectable row. It has to be
              ;; carried explicitly: this fn rebuilds the item map, so anything
              ;; not named here is dropped. That is what made "Select stage"
              ;; look like one of the stages under Create new.
              :section-label? section-label?
              :on-click     (fn [e]
                              (when-not disabled?
                                (cond
                                  (fn? on-click)
                                  (on-click e)

                                  (vector? event)
                                  (dispatch-action-event {:listing-id          listing-id
                                                          :requires-selection? requires-selection?
                                                          :event               event})

                                  :else
                                  (rf/dispatch [::event/trigger-toolbar-action
                                                listing-id
                                                id
                                                nil]))))}]
    (cond-> item
      (seq items)
      (assoc :items (mapv (partial toolbar-action-dropdown-item
                                   {:listing-id listing-id
                                    :disabled?  disabled?})
                          items)))))

(defn- toolbar-action-dropdown-items
  [{:keys [actions] :as args}]
  (mapv (partial toolbar-action-dropdown-item args) actions))

(defn- selection-pill-actions
  "The actions of the selection pill: one for each `:requires-selection?` toolbar
  action (bulk-edit, bulk-delete, ...). A click runs the same event as the item of
  the three dots menu, so the action gets the resolved selection."
  [{:keys [listing-id selection-actions can-interact?]}]
  (mapv (fn [{:keys [id label enabled? on-click event requires-selection? tooltip-text]
              :as action}]
          {:id        id
           :label     label
           :icon      (selection/pill-action-icon action)
           :title     tooltip-text
           :disabled? (or (not can-interact?)
                          (false? enabled?))
           :on-click  (fn [e]
                        (cond
                          (fn? on-click) (on-click e)
                          (vector? event) (dispatch-action-event {:listing-id          listing-id
                                                                  :requires-selection? requires-selection?
                                                                  :event               event})
                          :else (rf/dispatch [::event/trigger-toolbar-action listing-id id nil])))})
        selection-actions))

(defn- selection-pill-bar
  "The selection toolbar. It subscribes to the selection itself, so selecting a
   row (or card) updates only this bar and the row checkbox - not the whole
   listing. `clear-view-selection!` and `grid-view-id` are passed in because the
   clear button also tells the body views to drop their own selection state."
  [{:keys [listing-id grid-view-id clear-view-selection!]}]
  (let [selected-count    @(rf/subscribe [::sub/selected-count listing-id])
        selection-actions @(rf/subscribe [::sub/selection-actions listing-id])
        can-interact?     @(rf/subscribe [::sub/can-interact? listing-id])]
    [selection-pill/view
     {:count       selected-count
      :count-label (loc/tr :selected-item-count [selected-count])
      :actions     (selection-pill-actions {:listing-id        listing-id
                                            :selection-actions selection-actions
                                            :can-interact?     can-interact?})
      :clear-label (loc/tr :clear-selection)
      :move-label  (loc/tr :selection-pill-move)
      :disabled?   (not can-interact?)
      :on-clear    (fn [_]
                     (clear-view-selection!)
                     (rf/dispatch [::event/clear-selection listing-id])
                     (rf/dispatch [::grid-opensearch-event/clear-grid-selection grid-view-id]))}]))

(defn- listing-body-default
  "Renders the default listing body (grid/kanban/list) and subscribes to the
   preview entity id itself, so opening or changing the preview updates only the
   body subtree, not the whole listing."
  [{:keys [listing-id preview] :as props}]
  (let [preview-entity-id (when preview
                            @(rf/subscribe [::preview-sub/entity-id listing-id]))]
    [default-render-body (assoc props :preview-entity-id preview-entity-id)]))

(defn- view-mode-selector
  [{:keys [listing-id view-mode available-view-modes disabled? items icon-only?]}]
  (let [allowed-modes (set available-view-modes)
        items (or items default-view-mode-items)
        items (->> items
                   (filter (fn [{:keys [value]}]
                             (contains? allowed-modes value)))
                   vec)
        selected-item (or (some (fn [{:keys [value] :as item}]
                                  (when (= value view-mode)
                                    item))
                                items)
                          (first items))]
    (when (seq items)
      (if icon-only?
        [dropdown-menu/view
         {:items   (mapv (fn [{:keys [value icon title]}]
                           {:label        title
                            :selected?    (= value view-mode)
                            :on-click     (fn [_]
                                            (when-not disabled?
                                              (rf/dispatch [::event/set-view-mode listing-id value])))
                            :custom-item? true
                            :value        [:div {:class "tw2-flex tw2-items-center tw2-gap-2"}
                                           icon
                                           [:span title]]})
                         items)
          :props   {:hide-arrow? true
                    :align       :end}
          :trigger [:span
                    {:class      "tw2-inline-flex tw2-h-9 tw2-w-9 tw2-items-center tw2-justify-center tw2-rounded-[10px] tw2-text-secondary hover:tw2-bg-dropdown-bg-item-hover"
                     :aria-label (:title selected-item)}
                    (:icon selected-item)]}]
        [icon-choice/view
         {:value     view-mode
          :items     items
          :on-change (fn [mode]
                       (when-not disabled?
                         (rf/dispatch [::event/set-view-mode listing-id mode])))}]))))

(defn- filters-control
  [{:keys [listing-id
           entity-type
           pipeline-id
           initialized?
           context
           po-field-paths
           pinned-field-paths
           persisted-filters-by-field-path
           filters-config]}]
  (let [enabled? (true? (:enabled? filters-config))]
    (when (and enabled?
               initialized?)
      (let [editor-id (filters-editor-id listing-id filters-config)
            view-id (or (:view-id filters-config) listing-id)
            configured-runtime-paths (:runtime-field-paths filters-config)
            accessible-paths (->> (or (:accessible-fields context) [])
                                  (keep :field-path)
                                  vec)
            excluded-paths (set (concat (or po-field-paths [])
                                        (or pinned-field-paths [])))
            derived-runtime-paths (->> accessible-paths
                                       (remove (fn [field-path]
                                                 (contains? excluded-paths field-path)))
                                       vec)
            runtime-field-paths (if (some? configured-runtime-paths)
                                  (vec configured-runtime-paths)
                                  derived-runtime-paths)]
        [simple-filters/view
         {:editor-id editor-id
          :init-args {:editor-id                 editor-id
                      :view-id                   view-id
                      :entity-type               entity-type
                      :pipeline-id               pipeline-id
                      :account-id                (:account-id context)
                      :fields                    (:accessible-fields context)
                      :field-schemas             (:field-schemas filters-config)
                      :po-configured-field-paths po-field-paths
                      :pinned-field-paths        pinned-field-paths
                      :runtime-field-paths       runtime-field-paths
                      :filters-by-field-path     persisted-filters-by-field-path
                      :load-facets-event         (:load-facets-event filters-config)
                      :on-changed-event          [::event/persist-filters listing-id]}}]))))

(defn- kanban-sum-field-section
  "The choice of the number field that the Kanban columns and swimlanes sum."
  [{:keys [listing-id card-field-options]}]
  (let [sum-field @(rf/subscribe [::sub/kanban-sum-field listing-id])
        field-schema-of (partial registry/get-field-schema @session/tenant-schema)
        options (filterv (fn [{:keys [field-path] :as field}]
                           (let [data-type (or (:field/data-type field)
                                               (:data-type field))]
                             (and (listing-definition/sum-field? data-type)
                                  ;; A field that shows the days since a time has no
                                  ;; value in the index to sum.
                                  (nil? (some-> (field-path-normalized field-path)
                                                (os-utils/field-path->flat-alias)
                                                (days-since/since-alias)))
                                  ;; An amount of a link to many records has no sum by
                                  ;; currency.
                                  (or (not= :data.type/currency-value data-type)
                                      (totals/single-valued-path? (field-path-normalized field-path)
                                                                  field-schema-of)))))
                         card-field-options)
        option-button (fn [{:keys [key label field-path]}]
                        (let [selected? (= sum-field field-path)]
                          ^{:key key}
                          [button/component
                           {:variant  :ghost
                            :size     :sm
                            :class    (str "tw2-w-full tw2-justify-between "
                                           (when selected? "tw2-font-medium"))
                            :on-click (fn [_]
                                        (rf/dispatch [::event/set-kanban-sum-field listing-id field-path]))}
                           [:span {:class "tw2-truncate"} label]
                           (when selected?
                             [icons/check-icon {:class       "tw2-size-4 tw2-text-tertiary"
                                                :aria-hidden true}])]))]
    (when (seq options)
      [:div {:class "tw2-flex tw2-flex-col tw2-gap-1 tw2-border-t tw2-border-dropdown-border-default tw2-pt-2"}
       [:div {:class "tw2-px-3 tw2-text-xs tw2-font-medium tw2-text-tertiary"}
        (loc/tr :kanban-sum-field)]
       (into [:div {:class "tw2-flex tw2-max-h-[160px] tw2-flex-col tw2-overflow-y-auto tw2-gap-1"}]
             (cons (option-button {:key        "kanban-sum-none"
                                   :label      (loc/tr :kanban-sum-field/none)
                                   :field-path nil})
                   (map (fn [{:keys [field-path] :as field}]
                          (let [normalized-field-path (field-path-normalized field-path)]
                            (option-button {:key        (str "kanban-sum-" (field-path-key normalized-field-path))
                                            :label      (field-label field)
                                            :field-path normalized-field-path})))
                        options)))])))

(defn- card-settings-control
  [{:keys [listing-id
           view-mode
           initialized?
           can-interact?
           card-show-labels?
           selected-body-fields
           card-field-options]}]
  (when (and initialized?
             (contains? #{:kanban :list :gallery} view-mode)
             (seq card-field-options))
    (r/with-let [open?* (r/atom false)
                 search* (r/atom "")
                 container-node* (r/atom nil)
                 escape-key-handler (close-on-escape-handler open?*)
                 outside-click-handler (fn [event]
                                         (let [container-node @container-node*
                                               event-target (dg/target event)]
                                           (when (and @open?*
                                                      container-node
                                                      (not (dg/node-contains? container-node event-target)))
                                             (reset! open?* false))))]
      (dg/add-event-listener! js/document "mousedown" outside-click-handler)
      (dg/add-event-listener! js/document "keydown" escape-key-handler)
      (let [query (string/lower-case (or @search* ""))
            visible-field-options (if (string/blank? query)
                                    card-field-options
                                    (->> card-field-options
                                         (filter (fn [field]
                                                   (string/includes? (string/lower-case (field-label field))
                                                                     query)))
                                         vec))
            selected-keys (->> selected-body-fields
                               (map field-path-key)
                               set)
            field-buttons (map (fn [{:keys [field-path] :as field}]
                                 (let [normalized-field-path (field-path-normalized field-path)
                                       selected? (contains? selected-keys (field-path-key normalized-field-path))]
                                   ^{:key (str "card-field-setting-" (field-path-key normalized-field-path))}
                                   [:button
                                    {:type     "button"
                                     :class    (str "tw2-flex tw2-items-center tw2-justify-between tw2-rounded-[10px] tw2-px-3 tw2-py-2 tw2-text-left tw2-text-sm "
                                                    (if selected?
                                                      "tw2-bg-dropdown-bg-item-hover tw2-font-medium tw2-text-primary"
                                                      "hover:tw2-bg-dropdown-bg-item-hover tw2-text-secondary"))
                                     :on-click (fn [_]
                                                 (rf/dispatch [::event/toggle-card-body-field
                                                               listing-id
                                                               view-mode
                                                               normalized-field-path
                                                               selected-body-fields]))}
                                    [:span {:class "tw2-truncate"} (field-label field)]
                                    (when selected?
                                      [:i.fal.fa-check {:class "tw2-text-tertiary"}])]))
                               visible-field-options)]
        [:div {:class "tw2-relative tw2-shrink-0"
               :ref   (fn [node]
                        (reset! container-node* node))}
         [toolbar-button/view
          {:label     (loc/tr :card-fields)
           :icon      [:i.fal.fa-id-card]
           :expanded? @open?*
           :disabled? (not can-interact?)
           :on-click  (fn [_]
                        (swap! open?* not))}]
         ;; The popup closes when the listing does not allow changes, so none of
         ;; its buttons can change the view.
         (when (and @open?* can-interact?)
           [:div {:role       "dialog"
                  :aria-label (loc/tr :card-field-settings)
                  :class      "tw2-absolute tw2-left-0 tw2-top-full tw2-z-50 tw2-mt-1 tw2-flex tw2-min-w-[280px] tw2-flex-col tw2-gap-2 tw2-rounded-[16px] tw2-border tw2-border-dropdown-border-default tw2-bg-dropdown-list-bg tw2-text-primary tw2-p-3 tw2-shadow-[0_16px_36px_rgba(15,23,42,0.12)]"}
            [:button
             {:type     "button"
              :class    "tw2-flex tw2-items-center tw2-justify-between tw2-rounded-[10px] tw2-px-3 tw2-py-2 tw2-text-left tw2-text-sm hover:tw2-bg-dropdown-bg-item-hover tw2-text-secondary"
              :on-click (fn [_]
                          (rf/dispatch [::event/set-card-show-labels
                                        listing-id
                                        view-mode
                                        (not card-show-labels?)]))}
             [:span {:class "tw2-flex tw2-items-center tw2-gap-2"}
              [:i.fal.fa-tags]
              [:span (loc/tr :show-labels)]]
             [:span {:class "tw2-text-xs tw2-text-tertiary"}
              (if card-show-labels?
                (loc/tr :on)
                (loc/tr :off))]]
            [search-input/view
             {:value     @search*
              :on-change (fn [value]
                           (reset! search* value))}]
            [:div {:class "tw2-px-3 tw2-text-xs tw2-leading-5 tw2-text-tertiary"}
             (loc/tr :card-fields-display-limit)]
            (into [:div {:class "tw2-flex tw2-max-h-[320px] tw2-flex-col tw2-overflow-y-auto tw2-gap-1"}]
                  (if (seq field-buttons)
                    field-buttons
                    [[:div {:class "tw2-px-3 tw2-py-2 tw2-text-sm tw2-text-tertiary"}
                      (loc/tr :no-matches-found)]]))
            (when (= :kanban view-mode)
              [kanban-sum-field-section {:listing-id         listing-id
                                         :card-field-options card-field-options}])])])
      (finally
        (dg/remove-event-listener! js/document "keydown" escape-key-handler)
        (dg/remove-event-listener! js/document "mousedown" outside-click-handler)))))

(defn- view-name
  "The name of a saved view is text. The name of a default view is a
   localization key, so that it follows the language of the user."
  [named-view]
  (or (:named-view/name named-view)
      (some-> (:name-key named-view) loc/tr)))

(defn- share-member-row
  "One member in the share popover: a checkbox, an avatar and the name of the
   member. `on-toggle` gets the id of the member."
  [{:keys [account selected? on-toggle]}]
  (let [account-id (:db/id account)
        name (entity/display-string account)]
    [:label
     {:class "tw2-flex tw2-items-center tw2-gap-2 tw2-rounded-[10px] tw2-px-3 tw2-py-2 tw2-text-sm tw2-text-secondary hover:tw2-bg-dropdown-bg-item-hover tw2-cursor-pointer"}
     [checkbox/view
      {:value         selected?
       :style-variant :tw2
       :wrapper-class "tw2-shrink-0"
       :on-change     (fn [_] (on-toggle account-id))}]
     [avatar/view
      {:class "tw2-shrink-0"}
      (str (first (:account/first-name account))
           (first (:account/last-name account)))]
     [:span {:class "tw2-truncate"} name]]))

(defn- share-view-popover
  "The popover that shares a saved view with team members. A view component may
   read the session, so it reads the active accounts from the session and leaves
   out the current user. `view-id` is the id of the view to share. `on-close`
   closes the popover."
  [{:keys [view-id on-close]}]
  (r/with-let [share-with* (r/atom #{})
               search* (r/atom "")]
    (let [members (->> (session/active-accounts nil)
                       (remove (fn [a] (= (:db/id a) @session/account-id)))
                       (sort-by (fn [a] (string/lower-case (or (entity/display-string a) "")))))
          share-with @share-with*
          search (string/trim @search*)
          searching? (not (string/blank? search))
          visible (if searching?
                    (filter (fn [a]
                              (-> (or (entity/display-string a) "")
                                  string/lower-case
                                  (string/includes? (string/lower-case search))))
                            members)
                    members)
          all-selected? (and (seq members) (= (count members) (count share-with)))
          toggle! (fn [id]
                    (swap! share-with* (fn [s] (if (contains? s id) (disj s id) (conj s id)))))]
      [:div {:role       "dialog"
             :aria-label (loc/tr :share-view)
             :class      "tw2-absolute tw2-right-0 tw2-top-full tw2-z-50 tw2-mt-1 tw2-flex tw2-w-[300px] tw2-flex-col tw2-gap-2 tw2-rounded-[16px] tw2-border tw2-border-dropdown-border-default tw2-bg-dropdown-list-bg tw2-text-primary tw2-p-3 tw2-shadow-[0_16px_36px_rgba(15,23,42,0.12)]"
             :on-click   dg/stop-propagation}
       [:p {:class "tw2-text-sm tw2-font-semibold"} (loc/tr :share-view)]
       [:p {:class "tw2-text-xs tw2-text-tertiary"} (loc/tr :named-view-share-info-text)]
       (when (> (count members) 6)
         [search-input/view
          {:value     @search*
           :on-change (fn [value] (reset! search* (str value)))}])
       (when searching?
         [:p {:class "tw2-text-xs tw2-text-tertiary"} (loc/tr :search-results)])
       [:label
        {:class "tw2-flex tw2-items-center tw2-gap-2 tw2-px-3 tw2-py-2 tw2-text-sm tw2-text-secondary tw2-cursor-pointer"}
        [checkbox/view
         {:value         all-selected?
          :style-variant :tw2
          :wrapper-class "tw2-shrink-0"
          :on-change     (fn [_]
                           (reset! share-with*
                                   (if all-selected?
                                     #{}
                                     (set (map :db/id members)))))}]
        [:span (loc/tr :all-members-with-count [(count members)])]]
       (into [:div {:class "tw2-flex tw2-max-h-[260px] tw2-flex-col tw2-overflow-y-auto"}]
             (for [account visible]
               ^{:key (str "share-member-" (:db/id account))}
               [share-member-row
                {:account   account
                 :selected? (contains? share-with (:db/id account))
                 :on-toggle toggle!}]))
       [:div {:class "tw2-flex tw2-gap-2"}
        [button/sm-secondary-hollow
         {:class    "tw2-flex-1"
          :on-click (fn [_] (on-close))}
         (loc/tr :cancel)]
        [button/sm-primary-solid
         {:class     "tw2-flex-1"
          :disabled? (empty? share-with)
          :on-click  (fn [_]
                       (rf/dispatch [::event/share-saved-view
                                     {:view-id    view-id
                                      :share-with share-with}])
                       (on-close))}
         (loc/tr :share-view)]]])))

(defn- share-view-control
  "The share icon of a saved view and its popover. `view-id` is the id of the
   view to share."
  [{:keys [view-id]}]
  (r/with-let [open?* (r/atom false)
               container-node* (r/atom nil)
               escape-key-handler (close-on-escape-handler open?*)
               outside-click-handler (fn [event]
                                       (let [node @container-node*]
                                         (when (and @open?*
                                                    node
                                                    (not (dg/node-contains? node (dg/target event))))
                                           (reset! open?* false))))]
    (dg/add-event-listener! js/document "mousedown" outside-click-handler)
    (dg/add-event-listener! js/document "keydown" escape-key-handler)
    [:div {:class "tw2-relative"
           :ref   (fn [node] (reset! container-node* node))}
     [:i.fal.fa-share
      {:class    "tw2-cursor-pointer tw2-text-xs tw2-text-tertiary hover:tw2-text-secondary"
       :title    (loc/tr :share-view)
       :on-click (fn [e]
                   (dg/stop-propagation e)
                   (swap! open?* not))}]
     (when @open?*
       [share-view-popover
        {:view-id  view-id
         :on-close (fn [] (reset! open?* false))}])]
    (finally
      (dg/remove-event-listener! js/document "keydown" escape-key-handler)
      (dg/remove-event-listener! js/document "mousedown" outside-click-handler))))

(defn- saved-view-row
  [{:keys [listing-id editor-id named-view active? dirty? all-pipelines? editing-view-id* rename-draft* close-panel!]}]
  (let [view-id (:db/id named-view)
        editing? (= @editing-view-id* view-id)]
    [:div {:class (str "tw2-flex tw2-items-center tw2-gap-2 tw2-rounded-[10px] tw2-px-3 tw2-py-2 "
                       (when active? "tw2-bg-dropdown-bg-item-hover"))}
     (if editing?
       [:input
        {:class       "tw2-min-w-0 tw2-flex-1 tw2-rounded tw2-border tw2-border-field-border-default tw2-bg-field-bg-default tw2-px-2 tw2-py-1 tw2-text-sm"
         :auto-focus  true
         :value       @rename-draft*
         :on-click    dg/stop-propagation
         :on-change   (fn [e] (reset! rename-draft* (dg/event-target-value e)))
         :on-key-down (fn [e]
                        (case (dg/event-key e)
                          "Enter" (let [draft (string/trim @rename-draft*)]
                                    (when (seq draft)
                                      (rf/dispatch [::event/rename-saved-view {:view-id view-id :name draft}]))
                                    (reset! editing-view-id* nil))
                          "Escape" (reset! editing-view-id* nil)
                          nil))}]
       [:button
        {:type     "button"
         :class    (str "tw2-flex tw2-min-w-0 tw2-flex-1 tw2-items-center tw2-gap-2 tw2-truncate tw2-text-left tw2-text-sm hover:tw2-text-primary "
                        (if active? "tw2-font-medium tw2-text-primary" "tw2-text-secondary"))
         :on-click (fn [_]
                     (rf/dispatch [::event/apply-saved-view
                                   {:listing-id listing-id
                                    :editor-id  editor-id
                                    :named-view named-view}])
                     (close-panel!))}
        (when active?
          [:i.fal.fa-check {:class "tw2-shrink-0 tw2-text-xs tw2-text-tertiary"}])
        [:span {:class "tw2-truncate"} (view-name named-view)]
        (when all-pipelines?
          [:span {:class "tw2-shrink-0 tw2-rounded tw2-bg-badge-secondary-bg tw2-px-1.5 tw2-py-0.5 tw2-text-[11px] tw2-font-normal tw2-text-badge-secondary-text"}
           (loc/tr :saved-view-all-pipelines-tag)])])
     (when (and active?
                (not editing?)
                (not (:default-view? named-view)))
       [:div {:class "tw2-flex tw2-shrink-0 tw2-items-center tw2-gap-2"}
        (when dirty?
          [:i.fal.fa-save
           {:class    "tw2-cursor-pointer tw2-text-xs tw2-text-tertiary hover:tw2-text-secondary"
            :title    (loc/tr :save-view-changes)
            :on-click (fn [e]
                        (dg/stop-propagation e)
                        (rf/dispatch [::event/update-saved-view-settings
                                      {:view-id    view-id
                                       :listing-id listing-id
                                       :editor-id  editor-id}]))}])
        (when (and (int? view-id)
                   (not (session/is-read-only-user?)))
          [share-view-control {:view-id view-id}])
        [:i.fal.fa-pencil
         {:class    "tw2-cursor-pointer tw2-text-xs tw2-text-tertiary hover:tw2-text-secondary"
          :on-click (fn [e]
                      (dg/stop-propagation e)
                      (reset! rename-draft* (:named-view/name named-view))
                      (reset! editing-view-id* view-id))}]
        [:i.fal.fa-trash
         {:class    "tw2-cursor-pointer tw2-text-xs tw2-text-tertiary hover:tw2-text-secondary"
          :on-click (fn [e]
                      (dg/stop-propagation e)
                      (rf/dispatch [::event/delete-saved-view {:view-id view-id :listing-id listing-id}]))}]])]))

(defn- save-view-form
  "The form that saves the state of the listing as a new view: a name, the buttons
   Save and Cancel, and, for a listing of one pipeline among many, the scope of
   the view. The key Enter saves, and the key Escape cancels. Save is not
   possible while the name is empty. `on-save` gets the name and the scope."
  [{:keys [name* scope* scope-choice? on-save on-cancel]}]
  (let [save! (fn []
                (let [name (string/trim @name*)]
                  (when (seq name)
                    (on-save {:name  name
                              :scope @scope*}))))]
    [:div {:class "tw2-flex tw2-flex-col tw2-gap-2 tw2-px-1 tw2-pb-1"}
     [:input
      {:class       "tw2-w-full tw2-rounded tw2-border tw2-border-field-border-default tw2-bg-field-bg-default tw2-px-2 tw2-py-1.5 tw2-text-sm"
       :auto-focus  true
       :placeholder (loc/tr :view-name)
       :value       @name*
       :on-change   (fn [e] (reset! name* (dg/event-target-value e)))
       :on-key-down (fn [e]
                      (case (dg/event-key e)
                        "Enter" (save!)
                        "Escape" (on-cancel)
                        nil))}]
     (when scope-choice?
       [radio-group/radio-group-v2
        {:name      "saved-view-scope"
         :checked   @scope*
         :on-change (fn [scope] (reset! scope* scope))}
        [{:value :pipeline :content (loc/tr :saved-view-scope-pipeline)}
         {:value :entity-type :content (loc/tr :saved-view-scope-all-pipelines)}]])
     [:div {:class "tw2-flex tw2-justify-end tw2-gap-2"}
      [button/sm-secondary-hollow
       {:on-click (fn [_] (on-cancel))}
       (loc/tr :cancel)]
      [button/sm-primary-solid
       {:disabled? (string/blank? @name*)
        :on-click  (fn [_] (save!))}
       (loc/tr :save)]]]))

(defn- saved-views-control
  [{:keys [listing-id editor-id saved-views type-listing-id can-interact?]}]
  (r/with-let [open?* (r/atom false)
               creating?* (r/atom false)
               create-draft* (r/atom "")
               create-scope* (r/atom :pipeline)
               editing-view-id* (r/atom nil)
               rename-draft* (r/atom "")
               container-node* (r/atom nil)
               end-creating! (fn []
                               (reset! creating?* false)
                               (reset! create-draft* "")
                               (reset! create-scope* :pipeline))
               close-panel! (fn []
                              (reset! open?* false)
                              (end-creating!)
                              (reset! editing-view-id* nil))
               escape-key-handler (close-on-escape-handler open?*)
               outside-click-handler (fn [event]
                                       (let [container-node @container-node*
                                             event-target (dg/target event)]
                                         (when (and @open?*
                                                    container-node
                                                    (not (dg/node-contains? container-node event-target)))
                                           (close-panel!))))]
    (dg/add-event-listener! js/document "mousedown" outside-click-handler)
    (dg/add-event-listener! js/document "keydown" escape-key-handler)
    (let [dirty?         @(rf/subscribe [::sub/active-view-dirty? listing-id editor-id])
          active-view    (model/shown-active-view
                          {:active-view @(rf/subscribe [::sub/active-view listing-id])
                           :dirty?      dirty?})
          active-view-id (:db/id active-view)]
      [:div {:class "tw2-relative tw2-shrink-0"
             :ref   (fn [node]
                      (reset! container-node* node))}
       [toolbar-button/view
        {:label      (or (view-name active-view) (loc/tr :saved-views))
         :aria-label (loc/tr :saved-views)
         :icon       [:i.fal.fa-bookmark]
         :expanded?  @open?*
         :disabled?  (not can-interact?)
         :on-click   (fn [_]
                       (swap! open?* not))}]
       (when @open?*
         ;; Right-aligned: the saved-views button sits near the right end of the
         ;; toolbar, so a left-aligned popover opens rightward and the window
         ;; clips it. `right-0` opens it leftward from the button.
         [:div {:role       "dialog"
                :aria-label (loc/tr :saved-views)
                :class      "tw2-absolute tw2-right-0 tw2-top-full tw2-z-50 tw2-mt-1 tw2-flex tw2-min-w-[260px] tw2-flex-col tw2-gap-1 tw2-rounded-[16px] tw2-border tw2-border-dropdown-border-default tw2-bg-dropdown-list-bg tw2-text-primary tw2-p-2 tw2-shadow-[0_16px_36px_rgba(15,23,42,0.12)]"}
          (let [legacy-views (filterv :legacy-view? saved-views)
                default-views (filterv (fn [named-view]
                                         (and (:default-view? named-view)
                                              (not (:legacy-view? named-view))))
                                       saved-views)
                user-views (filterv (complement :default-view?) saved-views)
                view-row (fn [named-view]
                           ^{:key (str "saved-view-" (:db/id named-view))}
                           [saved-view-row {:listing-id       listing-id
                                            :editor-id        editor-id
                                            :named-view       named-view
                                            :active?          (= (:db/id named-view) active-view-id)
                                            :dirty?           dirty?
                                            :all-pipelines?   (model/all-pipelines-view?
                                                               {:named-view      named-view
                                                                :type-listing-id type-listing-id})
                                            :editing-view-id* editing-view-id*
                                            :rename-draft*    rename-draft*
                                            :close-panel!     close-panel!}])]
            (list
             (for [named-view default-views]
               (view-row named-view))
             (when (seq default-views)
               ^{:key "default-views-divider"}
               [:div {:class "tw2-my-1 tw2-h-px tw2-bg-dropdown-border-default"}])
             (when (seq legacy-views)
               (list
                ^{:key "legacy-views-title"}
                [:div {:class "tw2-px-3 tw2-pt-1 tw2-text-xs tw2-font-medium tw2-text-tertiary"}
                 (loc/tr :saved-views-from-old-grid)]
                (for [named-view legacy-views]
                  (view-row named-view))
                ^{:key "legacy-views-divider"}
                [:div {:class "tw2-my-1 tw2-h-px tw2-bg-dropdown-border-default"}]))
             (if (seq user-views)
               (for [named-view user-views]
                 (view-row named-view))
               ^{:key "no-saved-views"}
               [:div {:class "tw2-px-3 tw2-py-2 tw2-text-sm tw2-text-tertiary"}
                (loc/tr :no-saved-views)])))
          [:div {:class "tw2-my-1 tw2-h-px tw2-bg-dropdown-border-default"}]
          (if @creating?*
            [save-view-form
             {:name*         create-draft*
              :scope*        create-scope*
              :scope-choice? (some? type-listing-id)
              :on-cancel     end-creating!
              :on-save       (fn [{:keys [name scope]}]
                               (rf/dispatch [::event/create-saved-view
                                             {:listing-id listing-id
                                              :editor-id  editor-id
                                              :name       name
                                              :scope      scope}])
                               (end-creating!))}]
            [:button
             {:type     "button"
              :class    "tw2-flex tw2-items-center tw2-gap-2 tw2-rounded-[10px] tw2-px-3 tw2-py-2 tw2-text-left tw2-text-sm tw2-text-secondary hover:tw2-bg-dropdown-bg-item-hover"
              :on-click (fn [_] (reset! creating?* true))}
             [:i.fal.fa-plus]
             [:span (loc/tr :save-as-new-view)]])])])
    (finally
      (dg/remove-event-listener! js/document "keydown" escape-key-handler)
      (dg/remove-event-listener! js/document "mousedown" outside-click-handler))))

(defn- card-sort-mode-label
  [sort-mode]
  (case (kanban-sort/normalize-card-sort-mode sort-mode)
    :sort-by/alphanumerical (loc/tr :sort-title-az)
    :sort-by/value-desc (loc/tr :sort-title-za)
    (loc/tr :sort-title-az)))

(defn- card-sort-mode-indicator
  [sort-mode]
  (let [normalized-sort-mode (kanban-sort/normalize-card-sort-mode sort-mode)
        label (card-sort-mode-label normalized-sort-mode)
        icon-class (case normalized-sort-mode
                     :sort-by/value-desc "fal fa-sort-alpha-up"
                     "fal fa-sort-alpha-down")]
    [:span {:class      "tw2-inline-flex tw2-shrink-0 tw2-items-center tw2-justify-center tw2-text-tertiary"
            :title      label
            :aria-label label}
     [:i {:class       icon-class
          :aria-hidden "true"}]]))

(defn- kanban-card-sort-control
  [{:keys [listing-id view-mode initialized? kanban-card-sort-mode can-interact?]}]
  (when (and initialized?
             (= :kanban view-mode))
    (r/with-let [open?* (r/atom false)
                 container-node* (r/atom nil)
                 escape-key-handler (close-on-escape-handler open?*)
                 outside-click-handler (fn [event]
                                         (let [container-node @container-node*
                                               event-target (dg/target event)]
                                           (when (and @open?*
                                                      container-node
                                                      (not (dg/node-contains? container-node event-target)))
                                             (reset! open?* false))))]
      (dg/add-event-listener! js/document "mousedown" outside-click-handler)
      (dg/add-event-listener! js/document "keydown" escape-key-handler)
      (let [sort-options [[kanban-sort/alphanumerical (loc/tr :sort-title-az)]
                          [kanban-sort/value-desc (loc/tr :sort-title-za)]]]
        [:div {:class "tw2-relative tw2-shrink-0"
               :ref   (fn [node]
                        (reset! container-node* node))}
         [toolbar-button/view
          {:label     (loc/tr :card-sort)
           :icon      [:i.fal.fa-sort-alpha-down]
           :suffix    [card-sort-mode-indicator kanban-card-sort-mode]
           :expanded? @open?*
           :disabled? (not can-interact?)
           :on-click  (fn [_]
                        (swap! open?* not))}]
         (when @open?*
           [:div {:role       "dialog"
                  :aria-label (loc/tr :kanban-card-sorting)
                  :class      "tw2-absolute tw2-left-0 tw2-top-full tw2-z-50 tw2-mt-1 tw2-flex tw2-min-w-[220px] tw2-flex-col tw2-gap-1 tw2-rounded-[16px] tw2-border tw2-border-dropdown-border-default tw2-bg-dropdown-list-bg tw2-text-primary tw2-p-2 tw2-shadow-[0_16px_36px_rgba(15,23,42,0.12)]"}
            (for [[sort-mode label] sort-options]
              ^{:key (str "kanban-card-sort-mode-" (name sort-mode))}
              [:button
               {:type     "button"
                :class    (str "tw2-flex tw2-items-center tw2-justify-between tw2-rounded-[10px] tw2-px-3 tw2-py-2 tw2-text-left tw2-text-sm "
                               (if (= sort-mode kanban-card-sort-mode)
                                 "tw2-bg-dropdown-bg-item-hover tw2-font-medium tw2-text-primary"
                                 "hover:tw2-bg-dropdown-bg-item-hover tw2-text-secondary"))
                :on-click (fn [_]
                            (rf/dispatch [::event/set-kanban-card-sort-mode listing-id sort-mode])
                            (reset! open?* false))}
               [:span label]
               (when (= sort-mode kanban-card-sort-mode)
                 [:i.fal.fa-check {:class "tw2-text-tertiary"}])])])])
      (finally
        (dg/remove-event-listener! js/document "keydown" escape-key-handler)
        (dg/remove-event-listener! js/document "mousedown" outside-click-handler)))))

(defn title-menu-trigger
  "The title of the listing as the trigger of a menu, for example the name of the
   pipeline with the list of the other pipelines. `items` are the items of the
   dropdown menu. `aria-label` names the menu for a screen reader."
  [{:keys [title items aria-label disabled?]}]
  [dropdown-menu/view
   {:items items
    :props {:align         :start
            :trigger-class "tw2-max-w-full tw2-min-w-0"}
    :trigger [:span
              {:class      (str "tw2-inline-flex tw2-max-w-full tw2-min-w-0 tw2-items-center tw2-gap-2 tw2-rounded-[10px] tw2-px-2 tw2-py-1 "
                                ;; The tokens of the ghost button also have a dark value.
                                "tw2-text-btn-ghost-text-default hover:tw2-bg-btn-ghost-bg-hover "
                                (when disabled? "tw2-opacity-50 tw2-pointer-events-none"))
               :aria-label aria-label}
              [:i.fal.fa-code-branch {:class "tw2-shrink-0"}]
              [:span {:class "tw2-truncate"} title]
              [:i.fal.fa-angle-down {:class "tw2-shrink-0"}]]}])

(defn- listing-title
  "The title in the header row of the listing, or nil when the listing has no
   title. The title is the trigger of a menu when `title-menu` has items, for
   example the pipelines of the entity type. A long title ends with an ellipsis."
  [{:keys [title title-menu can-interact?]}]
  (let [title (when (string? title) (string/trim title))]
    (when (seq title)
      (if (seq (:items title-menu))
        [title-menu-trigger
         {:title      title
          :items      (:items title-menu)
          :aria-label (:aria-label title-menu)
          :disabled?  (not can-interact?)}]
        [:span {:class "tw2-truncate text-base-light-text"}
         title]))))

(def ^:private density-label-keys
  {:default :density-default
   :compact :density-compact})

(defn- density-menu-item
  "The item \"Density\" of the three dots menu of a listing in grid mode. It has one
   choice for each density of the grid. The choice that is on is marked."
  [{:keys [grid-id disabled?]}]
  (let [current @(rf/subscribe [::grid-opensearch-sub/density grid-id])]
    {:label     (loc/tr :density)
     :disabled? disabled?
     :items     (mapv (fn [density]
                        {:label     (loc/tr (get density-label-keys density))
                         :selected? (= density current)
                         :disabled? disabled?
                         :on-click  (fn [_]
                                      (rf/dispatch [::grid-opensearch-event/set-density grid-id density]))})
                      grid-density/densities)}))

(defn- listing-menu
  "The three dots menu at the end of the header row of the listing, or nil when
   the listing has no title."
  [{:keys [listing-id title view-mode can-interact? collapse-state on-expand-all on-collapse-all toolbar-actions]}]
  (let [title (when (string? title) (string/trim title))
        supports-collapse-actions? (contains? #{:kanban :list :gallery} view-mode)
        show-expand-all? (and supports-collapse-actions?
                              (not= collapse-state :all-expanded))
        show-collapse-all? (and supports-collapse-actions?
                                (not= collapse-state :all-collapsed))
        toolbar-action-items (toolbar-action-dropdown-items {:listing-id listing-id
                                                             :actions    toolbar-actions
                                                             :disabled?  (not can-interact?)})
        ;; What the listing can *do* comes first, so "Create new" - the first
        ;; toolbar action every listing registers - is the first thing in the
        ;; menu. The view utilities below it are housekeeping and were burying
        ;; it mid-list, which is what QA reported.
        view-items (cond-> (if (= view-mode :grid)
                             [{:label     (loc/tr :columns)
                               :disabled? (not can-interact?)
                               :on-click  (fn [_]
                                            (rf/dispatch [::columns-modal-event/open
                                                          (mode-view-id listing-id :grid)]))}
                              (density-menu-item {:grid-id   (mode-view-id listing-id :grid)
                                                  :disabled? (not can-interact?)})]
                             [])
                     true
                     (conj {:label     (loc/tr :reset-view)
                            :disabled? (not can-interact?)
                            :on-click  (fn [_]
                                         (rf/dispatch [::event/reset-view-config listing-id]))})
                     show-expand-all?
                     (conj {:label     (loc/tr :expand-all)
                            :disabled? (not can-interact?)
                            :on-click  (fn [_]
                                         (when (fn? on-expand-all)
                                           (on-expand-all)))})
                     show-collapse-all?
                     (conj {:label     (loc/tr :collapse-all)
                            :disabled? (not can-interact?)
                            :on-click  (fn [_]
                                         (when (fn? on-collapse-all)
                                           (on-collapse-all)))}))
        dropdown-items (if (seq toolbar-action-items)
                         (-> (vec toolbar-action-items)
                             (conj {:separator? true})
                             (into view-items))
                         view-items)]
    (when (seq title)
      [dropdown-menu/view
       {:items   dropdown-items
        :props   {:hide-arrow? true
                  :align       :end}
        :trigger [:span
                  {:class      "tw2-inline-flex tw2-h-9 tw2-w-9 tw2-items-center tw2-justify-center tw2-rounded-[10px] tw2-text-secondary hover:tw2-bg-dropdown-bg-item-hover"
                   :aria-label (loc/tr :reset-view)}
                  [:i.fal.fa-ellipsis-v]]}])))

(defn view
  [{:keys [listing-id class toolbar-left toolbar-center toolbar-right
           query-placeholder show-view-mode-selector? view-mode-items
           body render-body extra-view-props on-row-clicked on-card-clicked
           on-create-first-item
           title
           title-menu
           view-overrides
           preview]}]
  (r/with-let [collapse-command-seq* (r/atom 0)
               collapse-command* (r/atom nil)
               collapse-state* (r/atom :all-expanded)]
    (let [query-draft @(rf/subscribe [::sub/query-text listing-id])
          committed-query-text @(rf/subscribe [::sub/committed-query-text listing-id])
          entity-type @(rf/subscribe [::sub/entity-type listing-id])
          pipeline-id @(rf/subscribe [::sub/pipeline-id listing-id])
          context @(rf/subscribe [::sub/context listing-id])
          on-record-preview (when preview
                              (fn [record]
                                (when-let [open-event (preview/open-event
                                                       {:listing-id        listing-id
                                                        :entity-type       entity-type
                                                        :pipeline-id       pipeline-id
                                                        :accessible-fields (:accessible-fields context)
                                                        :record            record})]
                                  (rf/dispatch open-event))))
          group-by-field @(rf/subscribe [::sub/group-by-field listing-id])
          row-group-by-field @(rf/subscribe [::sub/row-group-by-field listing-id])
          row-group-sort-mode @(rf/subscribe [::sub/row-group-sort-mode listing-id])
          kanban-column-sort-mode @(rf/subscribe [::sub/kanban-column-sort-mode listing-id])
          kanban-card-sort-mode @(rf/subscribe [::sub/kanban-card-sort-mode listing-id])
          hide-empty-columns? @(rf/subscribe [::sub/hide-empty-columns? listing-id])
          card-show-labels? @(rf/subscribe [::sub/card-show-labels? listing-id])
          card-body-fields @(rf/subscribe [::sub/card-body-fields listing-id])
          groupable-fields @(rf/subscribe [::sub/groupable-fields listing-id])
          po-field-paths @(rf/subscribe [::sub/po-field-paths listing-id])
          pinned-field-paths @(rf/subscribe [::sub/pinned-field-paths listing-id])
          persisted-filters-by-field-path @(rf/subscribe [::sub/persisted-filters-by-field-path listing-id])
          filters-config @(rf/subscribe [::sub/filters-config listing-id])
          toolbar-filters-editor-id (filters-editor-id listing-id filters-config)
          saved-views @(rf/subscribe [::sub/saved-views listing-id])
          type-listing-id @(rf/subscribe [::sub/type-listing-id listing-id])
          view-mode @(rf/subscribe [::sub/view-mode listing-id])
          available-view-modes @(rf/subscribe [::sub/available-view-modes listing-id])
          listing-active-filter-count @(rf/subscribe [::sub/active-filter-count listing-id])
          toolbar-filters-by-field-path @(rf/subscribe [::simple-filters-sub/filters-by-field-path toolbar-filters-editor-id])
          toolbar-active-filter-count @(rf/subscribe [::simple-filters-sub/active-selection-count toolbar-filters-editor-id])
          can-interact? @(rf/subscribe [::sub/can-interact? listing-id])
          initialized? @(rf/subscribe [::sub/initialized? listing-id])
          card-field-options (->> (or (:accessible-fields context) [])
                                  (keep (fn [{:keys [field-path] :as field}]
                                          (let [normalized-field-path (field-path-normalized field-path)]
                                            (when normalized-field-path
                                              (assoc field :field-path normalized-field-path)))))
                                  (sort-by (fn [field]
                                             (string/lower-case (or (field-label field) ""))))
                                  vec)
          default-card-config (when entity-type
                                (listing-definition/card-config entity-type view-mode))
          effective-body-fields (let [selected-fields (->> (or card-body-fields [])
                                                           (keep field-path-normalized)
                                                           vec)
                                      default-fields (->> (or (:body-fields default-card-config) [])
                                                          (keep field-path-normalized)
                                                          vec)]
                                  ;; The default fields only when the user did not choose
                                  ;; the fields. A user who removed all the fields sees none.
                                  (if (nil? card-body-fields)
                                    default-fields
                                    selected-fields))
          dropdown-toolbar-actions @(rf/subscribe [::sub/dropdown-toolbar-actions listing-id])
          set-collapse-command! (fn [action]
                                  (let [next-id (swap! collapse-command-seq* inc)]
                                    (reset! collapse-state* (case action
                                                              :collapse-all :all-collapsed
                                                              :expand-all :all-expanded
                                                              :mixed))
                                    (reset! collapse-command* {:id     next-id
                                                               :action action})))
          clear-view-selection! (fn []
                                  (let [next-id (swap! collapse-command-seq* inc)]
                                    (reset! collapse-command* {:id     next-id
                                                               :action :clear-selection})))
          set-collapse-state! (fn [state]
                                (when (contains? #{:all-expanded :all-collapsed :mixed} state)
                                  (reset! collapse-state* state)))
          collapse-command @collapse-command*
          active-filter-count (if (true? (:enabled? filters-config))
                                toolbar-active-filter-count
                                listing-active-filter-count)]
      [:div {:class (str "tw2 w-full h-full flex flex-col " (or class ""))}
       [header/row
        {:title    (listing-title
                    {:title          title
                     :title-menu     title-menu
                     :can-interact?  can-interact?})
         :search   [query-input {:listing-id  listing-id
                                 :query-text  query-draft
                                 :disabled?   (not can-interact?)
                                 :placeholder query-placeholder}]
         :menu     (listing-menu
                    {:listing-id      listing-id
                     :title           title
                     :view-mode       view-mode
                     :collapse-state  @collapse-state*
                     :toolbar-actions dropdown-toolbar-actions
                     :on-expand-all   (fn []
                                        (set-collapse-command! :expand-all))
                     :on-collapse-all (fn []
                                        (set-collapse-command! :collapse-all))
                     :can-interact?   can-interact?})
         :controls [(when (true? show-view-mode-selector?)
                      [:div {:class "tw2-shrink-0 hidden 2xl:block"}
                       [view-mode-selector
                        {:listing-id           listing-id
                         :view-mode            view-mode
                         :available-view-modes available-view-modes
                         :items                view-mode-items
                         :disabled?            (not can-interact?)}]])
                    (when (true? show-view-mode-selector?)
                      [:div {:class "tw2-shrink-0 2xl:hidden"}
                       [view-mode-selector
                        {:listing-id           listing-id
                         :view-mode            view-mode
                         :available-view-modes available-view-modes
                         :items                view-mode-items
                         :icon-only?           true
                         :disabled?            (not can-interact?)}]])
                    [:div {:class "tw2-shrink-0 empty:tw2-hidden"}
                     [group-by/control
                      {:listing-id              listing-id
                       :entity-type             entity-type
                       :view-mode               view-mode
                       :initialized?            initialized?
                       :group-by-field          group-by-field
                       :kanban-column-sort-mode kanban-column-sort-mode
                       :hide-empty-columns?     hide-empty-columns?
                       :row-group-by-field      row-group-by-field
                       :row-group-sort-mode     row-group-sort-mode
                       :groupable-fields        groupable-fields
                       :can-interact?           can-interact?}]]
                    [:div {:class "tw2-shrink-0 empty:tw2-hidden"}
                     [kanban-card-sort-control
                      {:listing-id            listing-id
                       :view-mode             view-mode
                       :initialized?          initialized?
                       :kanban-card-sort-mode kanban-card-sort-mode
                       :can-interact?         can-interact?}]]
                    [:div {:class "tw2-shrink-0 empty:tw2-hidden"}
                     [card-settings-control
                      {:listing-id           listing-id
                       :view-mode            view-mode
                       :initialized?         initialized?
                       :can-interact?        can-interact?
                       :card-show-labels?    card-show-labels?
                       :selected-body-fields effective-body-fields
                       :card-field-options   card-field-options}]]
                    [:div {:class "tw2-shrink-0 empty:tw2-hidden"}
                     [filters-control
                      {:listing-id                      listing-id
                       :entity-type                     entity-type
                       :pipeline-id                     pipeline-id
                       :initialized?                    initialized?
                       :context                         context
                       :po-field-paths                  po-field-paths
                       :pinned-field-paths              pinned-field-paths
                       :persisted-filters-by-field-path persisted-filters-by-field-path
                       :filters-config                  filters-config}]]
                    [:div {:class "tw2-shrink-0 empty:tw2-hidden"}
                     [saved-views-control
                      {:listing-id      listing-id
                       :editor-id       toolbar-filters-editor-id
                       :saved-views     saved-views
                       :type-listing-id type-listing-id
                       :can-interact?   can-interact?}]]
                    toolbar-left
                    toolbar-center]
         :actions [(when (pos? active-filter-count)
                     [:button
                      {:type     "button"
                       :class    "text-xs text-base-light-text-paler underline hover:text-base-light-text disabled:cursor-not-allowed disabled:opacity-50"
                       :disabled (not can-interact?)
                       :on-click (fn [_]
                                   (if (true? (:enabled? filters-config))
                                     (rf/dispatch [::simple-filters-event/clear-all-filters toolbar-filters-editor-id])
                                     (rf/dispatch [::event/clear-filters listing-id])))}
                      (loc/tr :clear-all)])
                   (when (pos? active-filter-count)
                     [:span {:class "text-xs text-base-light-text-paler"}
                      (str active-filter-count " filters")])
                   toolbar-right]}]

       [:div {:class "flex flex-1 min-h-0"}
        [:div {:class "relative min-h-0 min-w-0 flex-1"}
         (if (fn? render-body)
           (render-body {:listing-id                    listing-id
                         :entity-type                   entity-type
                         :pipeline-id                   pipeline-id
                         :initialized?                  initialized?
                         :view-mode                     view-mode
                         :group-by-field                group-by-field
                         :row-group-by-field            row-group-by-field
                         :row-group-sort-mode           row-group-sort-mode
                         :kanban-column-sort-mode       kanban-column-sort-mode
                         :kanban-card-sort-mode         kanban-card-sort-mode
                         :hide-empty-columns?           hide-empty-columns?
                         :card-show-labels?             card-show-labels?
                         :card-body-fields              effective-body-fields
                         :available-view-modes          available-view-modes
                         :query-text                    committed-query-text
                         :query-draft                   query-draft
                         :context                       context
                         :can-interact?                 can-interact?
                         :active-filter-count           active-filter-count
                         :collapse-command              collapse-command
                         :on-collapse-state-changed     set-collapse-state!
                         :toolbar-filters-by-field-path (or toolbar-filters-by-field-path {})
                         :extra-view-props              extra-view-props})
           (if body
             body
             [listing-body-default {:listing-id                    listing-id
                                    :entity-type                   entity-type
                                    :pipeline-id                   pipeline-id
                                    :initialized?                  initialized?
                                    :view-mode                     view-mode
                                    :group-by-field                group-by-field
                                    :row-group-by-field            row-group-by-field
                                    :row-group-sort-mode           row-group-sort-mode
                                    :kanban-column-sort-mode       kanban-column-sort-mode
                                    :kanban-card-sort-mode         kanban-card-sort-mode
                                    :hide-empty-columns?           hide-empty-columns?
                                    :collapse-command              collapse-command
                                    :card-show-labels?             card-show-labels?
                                    :card-body-fields              effective-body-fields
                                    :available-view-modes          available-view-modes
                                    :on-collapse-state-changed     set-collapse-state!
                                    :context                       context
                                    :query-text                    committed-query-text
                                    :toolbar-filters-by-field-path (or toolbar-filters-by-field-path {})
                                    :on-row-clicked                on-row-clicked
                                    :on-card-clicked               on-card-clicked
                                    :on-record-preview             on-record-preview
                                    :preview                       preview
                                    :on-create-first-item          on-create-first-item
                                    :view-overrides                view-overrides}]))
         [selection-pill-bar {:listing-id            listing-id
                              :grid-view-id          (mode-view-id listing-id :grid)
                              :clear-view-selection! clear-view-selection!}]]
        (when preview
          [preview/view
           {:listing-id   listing-id
            :on-open-page (:on-open-page preview)
            :actions-fn   (:actions-fn preview)}])]])))
